import * as React from 'react'
import { createPortal } from 'react-dom'
import moment from 'moment'
import {
  ChartScanListFilter,
  useChartScans,
  useDeleteChartScan,
  useDeleteDiscs,
} from '@/lib/api/company/chartScan'
import {
  FaIcon,
  faChartPieSimpleSolid,
  faCircleCheck,
  faCircleExclamation,
  faClockSolid,
  faPenSolid,
  faRefreshSolid,
  faTrashSolid,
} from '@/lib/fa'
import { toast } from '@/lib/toasts/api'
import { TableHeader } from '@/modules/drivers/shared/tableItems/TableHeader'
import TableTextRow from '@/modules/drivers/shared/tableItems/TableTextRow'
import { TableHeaderColumn } from '@/modules/drivers/types/table'
import TachoDisc from '@/modules/drivers/tabs/activities/tacho_disc/TachoDisc'
import {
  fmt,
  useChartT,
} from '@/modules/imports/container/scan-discs-popup-container/i18n'
import { useConfirm } from '@/modules/shared/context/ConfirmContext'
import { useLocalizeSafe } from '@/modules/shared/context/localize/LocalizeContext'
import { getApiErrorMessage } from '@/modules/shared/utils/apiError'
import { Button } from '@/ui/button'
import { Spinner } from '@/ui/feedback'
import { PaginationControls } from '@/ui/navigation/pagination/PaginationControls'
import { getPageNumbers } from '@/ui/navigation/pagination/pagination.utils'

export function ScannedChartsSection({ companyId }: { companyId: string }) {
  const T = useChartT()
  const { t } = useLocalizeSafe()
  const confirm = useConfirm()
  const [filter, setFilter] = React.useState<ChartScanListFilter>('Pending')
  const [page, setPage] = React.useState(1)
  const [discId, setDiscId] = React.useState<number | null>(null)
  const [deletingDiscId, setDeletingDiscId] = React.useState<number | null>(null)
  const [deletingScanId, setDeletingScanId] = React.useState<number | null>(null)
  const { data, isLoading, isError, isFetching, refetch } = useChartScans(
    companyId,
    filter,
    page,
  )
  const deleteDisc = useDeleteDiscs(companyId)
  const deleteScan = useDeleteChartScan(companyId)
  const pages = Math.max(1, Math.ceil((data?.totalFiles ?? 0) / 25))

  React.useEffect(() => {
    if (data && page > pages) setPage(pages)
  }, [data, page, pages])

  const handleDeleteScan = async (scanId: number, fileName: string) => {
    const ok = await confirm({
      title: T('listDeleteScanTitle', 'Usunąć plik?'),
      message: fmt(
        T(
          'listDeleteScanMessage',
          'Plik {0} zostanie usunięty z listy. Nie wykryto w nim tarczek, więc żadne dane kierowców nie zostaną zmienione.',
        ),
        fileName,
      ),
      confirmText: T('listDeleteConfirm', 'Usuń'),
      cancelText: T('cancel', 'Anuluj'),
    })
    if (!ok) return

    setDeletingScanId(scanId)
    try {
      await deleteScan.mutateAsync(scanId)
      toast.success({ text: T('listDeleteScanSuccess', 'Plik usunięty.') })
    } catch (err) {
      toast.error({
        text:
          getApiErrorMessage(err) ||
          T('listDeleteScanError', 'Nie udało się usunąć pliku.'),
      })
    } finally {
      setDeletingScanId(null)
    }
  }

  const handleDelete = async (
    scanId: number,
    disc: { discId: number; discNumber: number },
    fileName: string,
  ) => {
    const ok = await confirm({
      title: T('listDeleteTitle', 'Usunąć wykresówkę?'),
      message: fmt(
        T(
          'listDeleteMessage',
          'Tarczka {0} z pliku {1} zostanie trwale usunięta razem z zapisanymi aktywnościami kierowcy. Tej operacji nie można cofnąć.',
        ),
        disc.discNumber,
        fileName,
      ),
      confirmText: T('listDeleteConfirm', 'Usuń'),
      cancelText: T('cancel', 'Anuluj'),
    })
    if (!ok) return

    setDeletingDiscId(disc.discId)
    try {
      await deleteDisc.mutateAsync({ scanId, discId: disc.discId })
      if (discId === disc.discId) setDiscId(null)
      toast.success({ text: T('listDeleteSuccess', 'Wykresówka usunięta.') })
    } catch (err) {
      toast.error({
        text:
          getApiErrorMessage(err) ||
          T('listDeleteError', 'Nie udało się usunąć wykresówki.'),
      })
    } finally {
      setDeletingDiscId(null)
    }
  }

  const filters: { value: ChartScanListFilter; label: string }[] = [
    { value: 'Pending', label: T('listPending', 'Do uzupełnienia') },
    { value: 'Completed', label: T('listCompleted', 'Zapisane') },
    { value: 'All', label: T('listAll', 'Wszystkie') },
  ]
  const columns: TableHeaderColumn[] = [
    T('listDiscId', 'Numer tarczki'),
    T('listFileId', 'Numer pliku'),
    T('listFileName', 'Nazwa pliku'),
    T('listPosition', 'Tarczka w pliku'),
    T('listStatus', 'Status'),
    T('listDate', 'Data'),
    T('listDriver', 'Kierowca'),
    T('listVehicle', 'Pojazd'),
    T('listUploadedBy', 'Wczytany przez'),
    T('listUploadedAt', 'Data wczytania'),
    T('listActions', 'Akcje'),
  ].map((title) => ({ title, key: null, type: 'string', sort: false }))

  return (
    <section className="tw-overflow-hidden tw-rounded-xl tw-border tw-border-content-base/10 tw-bg-white">
      <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-3 tw-border-b tw-border-content-base/10 tw-p-4">
        <h2 className="tw-m-0 tw-flex tw-items-center tw-gap-2 tw-text-base tw-font-semibold tw-text-content-base">
          <FaIcon icon={faChartPieSimpleSolid} className="tw-text-primary" />
          {T('listTitle', 'Zeskanowane wykresówki')}
        </h2>
        <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-2">
          <div className="tw-inline-flex tw-flex-wrap tw-gap-1 tw-rounded-md tw-bg-content-primary tw-p-1">
            {filters.map((option) => (
              <Button
                key={option.value}
                type="button"
                aria-pressed={filter === option.value}
                onClick={() => {
                  setFilter(option.value)
                  setPage(1)
                }}
                size="sm"
                variant={filter === option.value ? 'primary' : 'tertiary'}
              >
                {option.label}
              </Button>
            ))}
          </div>
          <Button
            type="button"
            disabled={isFetching}
            onClick={() => void refetch()}
            size="sm"
            variant="tertiary"
            mode="outline"
            faLeadingIcon={faRefreshSolid}
          >
            {T('listRefresh', 'Odśwież')}
          </Button>
        </div>
      </div>

      {isLoading ? (
        <div className="tw-flex tw-justify-center tw-p-8">
          <Spinner />
        </div>
      ) : isError ? (
        <p role="alert" className="tw-p-4 tw-text-error">
          {T(
            'listLoadError',
            'Nie udało się wczytać listy wykresówek. Spróbuj ponownie.',
          )}
        </p>
      ) : !data?.items.length ? (
        <p className="tw-p-6 tw-text-center tw-text-content-base/70">
          {T('listEmpty', 'Brak wykresówek dla wybranego statusu.')}
        </p>
      ) : (
        <>
          <div className="tw-overflow-x-auto">
            <table className="tw-w-full tw-table-auto tw-border-collapse tw-text-left">
              <TableHeader
                columns={columns}
                sortConfig={null}
                disabledCheckboxToggle
                fontSize="md"
              />
              <tbody>
                {data.items.flatMap((scan) =>
                  scan.discs.length === 0 ? (
                    <tr
                      key={`scan-${scan.scanId}`}
                      className="notches-border--right tw-border-b tw-border-content-base/10 tw-bg-white hover:tw-bg-primary/10"
                    >
                      <TableTextRow pointer={false}>-</TableTextRow>
                      <TableTextRow pointer={false}>{scan.scanId}</TableTextRow>
                      <TableTextRow pointer={false}>
                        {scan.fileName}
                      </TableTextRow>
                      <TableTextRow pointer={false}>
                        0 / {scan.discCount}
                      </TableTextRow>
                      <td
                        colSpan={4}
                        className="tw-whitespace-nowrap tw-px-3 tw-py-3.5 tw-text-sm tw-font-medium tw-text-warning"
                      >
                        <span className="tw-inline-flex tw-items-center tw-gap-2">
                          <FaIcon icon={faCircleExclamation} />
                          {T('listNoDiscs', 'Nie wykryto tarczek')}
                        </span>
                      </td>
                      <TableTextRow pointer={false}>
                        {scan.uploadedBy || '-'}
                      </TableTextRow>
                      <TableTextRow pointer={false}>
                        {scan.uploadedAt
                          ? moment(scan.uploadedAt).format('DD.MM.YYYY HH:mm')
                          : '-'}
                      </TableTextRow>
                      <td className="tw-px-3 tw-py-2 tw-text-right">
                        <button
                          type="button"
                          disabled={deletingScanId === scan.scanId}
                          onClick={() =>
                            void handleDeleteScan(scan.scanId, scan.fileName)
                          }
                          aria-label={T('listDeleteScan', 'Usuń plik')}
                          title={T('listDeleteScan', 'Usuń plik')}
                          className="tw-inline-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-md tw-text-neutral hover:tw-bg-base-200 hover:tw-text-error focus-visible:tw-outline focus-visible:tw-outline-2 focus-visible:tw-outline-error disabled:tw-cursor-not-allowed disabled:tw-opacity-50"
                        >
                          <FaIcon icon={faTrashSolid} />
                        </button>
                      </td>
                    </tr>
                  ) : (
                    scan.discs.map((disc) => (
                      <tr
                        key={disc.discId}
                        onDoubleClick={() => setDiscId(disc.discId)}
                        className="notches-border--right tw-cursor-pointer tw-border-b tw-border-content-base/10 tw-bg-white hover:tw-bg-primary/10"
                      >
                        <TableTextRow>{disc.discId}</TableTextRow>
                        <TableTextRow>{scan.scanId}</TableTextRow>
                        <TableTextRow>{scan.fileName}</TableTextRow>
                        <TableTextRow>
                          {disc.discNumber} / {scan.discCount}
                        </TableTextRow>
                        <TableTextRow>
                          <span
                            className={`tw-inline-flex tw-items-center tw-gap-2 ${disc.status === 'Ignored' ? 'tw-text-warning' : 'tw-text-success'}`}
                          >
                            <FaIcon
                              icon={
                                disc.status === 'Ignored'
                                  ? faClockSolid
                                  : faCircleCheck
                              }
                            />
                            {disc.status === 'Ignored'
                              ? T('listPending', 'Do uzupełnienia')
                              : T('listCompleted', 'Zapisane')}
                          </span>
                        </TableTextRow>
                        <TableTextRow>
                          {disc.chartDate
                            ? moment(disc.chartDate).format('DD.MM.YYYY')
                            : '-'}
                        </TableTextRow>
                        <TableTextRow>{disc.driverName || '-'}</TableTextRow>
                        <TableTextRow>
                          {disc.vehicleRegistration || '-'}
                        </TableTextRow>
                        <TableTextRow>{scan.uploadedBy || '-'}</TableTextRow>
                        <TableTextRow>
                          {scan.uploadedAt
                            ? moment(scan.uploadedAt).format('DD.MM.YYYY HH:mm')
                            : '-'}
                        </TableTextRow>
                        <td className="tw-px-3 tw-py-2 tw-text-right">
                          <div className="tw-inline-flex tw-items-center tw-gap-1">
                            <button
                              type="button"
                              onClick={() => setDiscId(disc.discId)}
                              aria-label={T('listEdit', 'Otwórz do obróbki')}
                              title={T('listEdit', 'Otwórz do obróbki')}
                              className="tw-inline-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-md tw-text-neutral hover:tw-bg-base-200 hover:tw-text-primary focus-visible:tw-outline focus-visible:tw-outline-2 focus-visible:tw-outline-primary"
                            >
                              <FaIcon icon={faPenSolid} />
                            </button>
                            <button
                              type="button"
                              disabled={deletingDiscId === disc.discId}
                              onClick={(event) => {
                                event.stopPropagation()
                                void handleDelete(
                                  scan.scanId,
                                  disc,
                                  scan.fileName,
                                )
                              }}
                              onDoubleClick={(event) => event.stopPropagation()}
                              aria-label={T('listDelete', 'Usuń wykresówkę')}
                              title={T('listDelete', 'Usuń wykresówkę')}
                              className="tw-inline-flex tw-h-8 tw-w-8 tw-items-center tw-justify-center tw-rounded-md tw-text-neutral hover:tw-bg-base-200 hover:tw-text-error focus-visible:tw-outline focus-visible:tw-outline-2 focus-visible:tw-outline-error disabled:tw-cursor-not-allowed disabled:tw-opacity-50"
                            >
                              <FaIcon icon={faTrashSolid} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  ),
                )}
              </tbody>
            </table>
          </div>
          <div className="tw-flex tw-flex-wrap tw-items-center tw-justify-between tw-gap-3 tw-px-4 tw-py-3 tw-text-sm tw-text-content-base">
            <span className="tw-text-neutral">
              {T('listFiles', 'Pliki')}: {data.totalFiles}
            </span>
            <PaginationControls
              currentPage={page}
              totalPages={pages}
              disabled={isFetching}
              pages={getPageNumbers(page, pages)}
              onPrevious={() => setPage(page - 1)}
              onNext={() => setPage(page + 1)}
              onPageClick={setPage}
              t={(key) => t(key)}
            />
          </div>
        </>
      )}

      {discId !== null &&
        createPortal(
          <TachoDisc
            key={`${companyId}-${discId}`}
            companyId={companyId}
            discId={discId}
            includeSiblings
            onSaved={() => void refetch()}
            onClose={() => {
              setDiscId(null)
              void refetch()
            }}
          />,
          document.body,
        )}
    </section>
  )
}
